import clsx from "clsx";
import type { HTMLInputTypeAttribute } from "react";
import { forwardRef, useId } from "react";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import styles from "./InputField.module.scss";
import { useLingui } from "@lingui/react/macro";

interface IInputField {
  autoComplete?: string;
  errorMessage?: string;
  formatHint?: string;
  icon?: React.ReactNode;
  name: string;
  label: string;
  hideLabel?: boolean; // Only to be used when part of a `<fieldset>` with a `<legend>` that acts as label
  showOptionalLabel: boolean;
  description?: React.ReactNode;
  suggestions?: string[];
  type?: HTMLInputTypeAttribute;
  pattern?: string;
  width?: "full" | "fit";
  outerWidth?: "full" | "fit";
  inputWidth?: string;
  size?: "default" | "compact";
  prefix?: string;
  suffix?: string;
  readOnly?: boolean;
}

const InputField = forwardRef<HTMLInputElement | null, IInputField>(
  (
    {
      autoComplete,
      errorMessage,
      formatHint,
      name,
      label,
      hideLabel,
      showOptionalLabel,
      description,
      icon,
      suggestions,
      type,
      pattern,
      width = ["date", "number", "time"].includes(type ?? "") ? "fit" : "full",
      outerWidth,
      inputWidth,
      size = "default",
      prefix,
      suffix,
      readOnly,
      ...rest
    },
    ref,
  ) => {
    const { t } = useLingui();
    const id = useId();
    const datalistId = useId();
    const descriptionId = useId();

    const displayLabel =
      `${label} ${showOptionalLabel ? t`(valfri)` : ""}`.trim();

    const effectiveWidth = outerWidth ?? width;

    return (
      <div
        className={clsx(
          styles.inputFieldWrapper,
          styles[`outerWidth-${effectiveWidth}`],
        )}
      >
        {hideLabel ? null : icon ? (
          <div className={styles.labelWithIcon}>
            {icon}
            <label className={styles.label} htmlFor={id}>
              {displayLabel}
            </label>
          </div>
        ) : (
          <label className={styles.label} htmlFor={id}>
            {displayLabel}
          </label>
        )}
        {description ? (
          <p id={descriptionId} className={styles.description}>
            {description}
          </p>
        ) : undefined}
        {errorMessage ? (
          <div className={clsx(styles.errorMessage, styles[`size-${size}`])}>
            <ErrorMessage message={errorMessage} weight="bold" />
          </div>
        ) : undefined}
        <div
          className={clsx(
            styles.inputWrapper,
            styles[`size-${size}`],
            hideLabel && styles.hiddenLabel,
          )}
        >
          {prefix ? <div className={styles.prefix}>{prefix}</div> : undefined}
          <input
            aria-invalid={Boolean(errorMessage) || undefined}
            aria-describedby={description ? descriptionId : undefined}
            aria-label={hideLabel ? label : undefined}
            // oxlint-disable-next-line react/purity
            autoComplete={autoComplete || Math.random().toString(36)}
            className={styles.input}
            style={inputWidth ? { width: inputWidth } : undefined}
            formNoValidate={true}
            id={id}
            list={suggestions ? datalistId : undefined}
            name={name}
            placeholder={formatHint}
            ref={ref}
            type={type}
            pattern={pattern}
            readOnly={readOnly}
            {...rest}
          />
          {suffix ? <div className={styles.suffix}>{suffix}</div> : undefined}
        </div>
        {suggestions ? (
          <datalist id={datalistId}>
            {suggestions.map((suggestion) => (
              <option key={suggestion} value={suggestion}>
                {suggestion}
              </option>
            ))}
          </datalist>
        ) : undefined}
      </div>
    );
  },
);
InputField.displayName = "InputField";

export default InputField;
